<template>
  <form id="kiosks.form" @submit.prevent="submitForm">
    <BaseModal v-model="modal" id="kiosks.modal.form">
      <template #header>
        <div class="flex items-center">
          <h3 class="text-lg font-bold text-gray-900 mb-0 mr-3">
            {{
              form?.id ? translate("Edit Kiosk") : translate("Add New Kiosk")
            }}
          </h3>
          <BaseButton
            v-if="form.id"
            size="xs"
            :variant="initialValue.available ? 'success' : 'danger'"
            rounded="full"
            type="button"
            @click="deactivateKiosk"
          >
            {{
              initialValue.available ? translate("Not Used") : translate("Used")
            }}
          </BaseButton>
        </div>
      </template>

      <div>
        <label for="">{{ translate("Kiosk Device Name") }}</label>
        <BaseField
          id="kiosks.modal.form.input.name"
          type="input"
          v-model="form.label"
          name="form.label"
          :error="formErrors.label ? true : false"
          :hint="formErrors.label ? formErrors.label : ''"
        />
      </div>
      <div>
        <label for="">{{ translate("Location") }}</label>
        <BaseField
          type="searchable-select"
          @input="(val) => (form['location-id'] = val)"
          :searchFunction="debounceSearchLocation"
          placeholder="Search location..."
          :error="formErrors.location ? true : false"
          :hint="formErrors.location ? formErrors.location : ''"
          :value="form['location-id']"
          :options="locationOptions"
        />
      </div>
      <div>
        <label for="">{{ translate("Device") }}</label>
        <BaseField
          id="kiosks.modal.form.select.device"
          type="select"
          :options="devices"
          v-model="form.type"
          :error="formErrors.type ? true : false"
          :hint="formErrors.type ? formErrors.type : ''"
        />
      </div>

      <template v-if="form.type == 'ipad'">
        <div>
          <label for="">{{ translate("Printer Manufacturer") }}</label>
          <BaseField
            id="kiosks.modal.form.select.manufacturers"
            type="select"
            :options="manufacturers"
            v-model="form['printer-settings']['printer-manufacturer']"
          />
        </div>
        <div>
          <label for="">{{ translate("Receipt Mode") }}</label>
          <BaseField
            id="kiosks.modal.form.select.modes"
            type="select"
            :options="modes"
            v-model="form['printer-settings']['receipt-mode']"
          />
        </div>
        <div>
          <label for="">{{ translate("Printer IP address") }}</label>
          <BaseField
            id="kiosks.modal.form.input.ip"
            type="input"
            v-model="form['printer-settings']['ip-address']"
          />
        </div>
      </template>

      <template v-if="form['type'] === 'windows'">
        <div>
          <label for="">{{ translate("UID from SignageOS") }}</label>
          <BaseField
            id="kiosks.modal.form.input.uuid"
            type="input"
            v-model="form.uuid"
            :error="formErrors.uuid ? true : false"
            :hint="formErrors.uuid ? formErrors.uuid : ''"
          />
        </div>
      </template>

      <div v-if="isCustomerRequired && form['location-id']">
        <label for="">{{ translate("Select Customer") }}</label>
        <BaseField
          type="searchable-select"
          @input="(val) => (form['customer-id'] = val)"
          :searchFunction="debounceSearchCustomer"
          placeholder="Search customer..."
          :error="formErrors.customer ? true : false"
          :hint="formErrors.customer ? formErrors.customer : ''"
          :value="form['customer-id']"
          :options="customersOptions"
        />
      </div>

      <div>
        <label for="">{{
          translate("Card Payment Terminal Manufacturer")
        }}</label>
        <BaseField
          id="kiosks.modal.form.select.terminal"
          type="select"
          :options="terminalOptions"
          v-model="form['payment-gateway']"
        />
      </div>
      <div class="grid grid-cols-2 gap-4">
        <template v-if="form['payment-gateway'] === 'adyen'">
          <div>
            <label for="">{{ translate("Terminal POIID") }}</label>
            <BaseField
              id="kiosks.modal.form.input.poiid"
              v-model="form['payment-settings']['terminal-poiid']"
            />
          </div>
          <div>
            <label for="">{{ translate("Key Version") }}</label>
            <BaseField
              id="kiosks.modal.form.input.keyversion"
              v-model="form['payment-settings']['key-version']"
            />
          </div>
          <div>
            <label for="">{{ translate("Key Identification") }}</label>
            <BaseField
              id="kiosks.modal.form.input.keyid"
              v-model="form['payment-settings']['key-identification']"
            />
          </div>
          <div>
            <label for="">{{ translate("Key Passphrase") }}</label>
            <BaseField
              id="kiosks.modal.form.input.keypass"
              v-model="form['payment-settings']['key-passphrase']"
            />
          </div>
        </template>
        <template
          v-if="
            (form['payment-gateway'] === 'foodics-pay' &&
              form['type'] === 'ipad') ||
            form['payment-gateway'] === 'paymob'
          "
        >
          <div>
            <label for="">{{ translate("Terminal ID") }}</label>
            <BaseField
              id="kiosks.modal.form.input.terminalid"
              v-model="form['terminal-id']"
            />
          </div>
        </template>
        <template
          v-if="
            (form['payment-gateway'] === 'geidea-pax' ||
              form['payment-gateway'] === 'geidea-verifone') &&
            (form['type'] === 'ipad' || form['type'] === 'windows')
          "
        >
          <div>
            <label for="">{{
              translate("IP Address of Card Payment Terminal")
            }}</label>
            <BaseField
              id="kiosks.modal.form.input.ipaddress"
              v-model="form['ip-address']"
            />
          </div>
          <div>
            <label for="">{{
              translate("Port of Card Payment Terminal")
            }}</label>
            <BaseField id="kiosks.modal.form.input.port" v-model="form.port" />
          </div>
        </template>
        <template
          v-if="
            form['payment-gateway'] === 'intersoft' &&
            (form['type'] === 'ipad' || form['type'] === 'windows')
          "
        >
          <div>
            <label for="">{{
              translate("IP Address of Card Payment Terminal")
            }}</label>
            <BaseField
              id="kiosks.modal.form.input.ipaddress"
              v-model="form['ip-address']"
            />
          </div>
        </template>
      </div>
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label for="">{{ translate("Lock Settings Screen") }}</label>
          <BaseField
            type="toggle"
            id="kiosks.modal.form.toggle.passwordprotected"
            v-model="form['is-settings-password-protected']"
          />
        </div>
        <div>
          <label for="">{{ translate("Maintenance Mode") }}</label>
          <BaseField
            type="toggle"
            id="kiosks.modal.form.toggle.maintenancemode"
            v-model="form['is-maintenance-mode']"
          />
        </div>
      </div>

      <template #footer>
        <BaseButton
          id="kiosks.modal.form.button.close"
          @click="modal = false"
          >{{ translate("Close") }}</BaseButton
        >
        <BaseButton
          :loading="loading"
          id="kiosks.modal.form.button.save"
          variant="primary"
          >{{ translate("Save") }}</BaseButton
        >
      </template>
    </BaseModal>
  </form>
</template>
<script>
import BaseButton from "@/components/V2/BaseButton.vue";
import BaseModal from "@/components/V2/Modals/BaseModal.vue";
import BaseField from "@/components/V2/BaseField.vue";
import { translations, notificationAlerts } from "@/mixins";
import { Location as LocationService, Customer, Device } from "@/services/SOLO";
import cloneDeep from "lodash/cloneDeep";
import _ from "lodash";

const defaultForm = {
  id: "",
  label: "",
  status: "",
  available: false,
  "location-id": "",
  "customer-id": "",
  "payment-gateway": "",
  uuid: "",
  "is-settings-password-protected": false,
  "ip-address": "",
  "terminal-id": "",
  type: "ipad",
  port: "",
  "printer-settings": {
    "printer-manufacturer": "",
    "receipt-mode": "",
    "ip-address": "",
    "printer-name": "",
  },
  "payment-settings": {
    "terminal-poiid": "",
    "key-version": "",
    "key-identification": "",
    "key-passphrase": "",
  },
  "is-maintenance-mode": false,
};

export default {
  name: "KioskFormModal",
  mixins: [translations, notificationAlerts],
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    initialValue: {
      default: null,
    },
  },
  components: {
    BaseModal,
    BaseButton,
    BaseField,
  },
  data() {
    return {
      form: cloneDeep(defaultForm),
      devices: [
        { label: "Counter-Top (iPad)", value: "ipad" },
        { label: "Tower (Windows)", value: "windows" },
      ],
      manufacturers: [
        { label: "SUNMI", value: "sunmi" },
        { label: "Epson", value: "epson" },
      ],
      modes: [
        { label: "SMS", value: "sms" },
        { label: "QR", value: "qr" },
        { label: "PRINTER", value: "printer" },
      ],
      terminal: [
        { label: "Intersoft (legacy)", value: "intersoft" },
        { label: "A920 (legacy)", value: "geidea-pax" },
        { label: "VX690 (legacy)", value: "geidea-verifone" },
      ],
      windowsTerminal: [
        { label: "A920 (legacy)", value: "geidea-pax" },
        { label: "VX690 (legacy)", value: "geidea-verifone" },
        { label: "Adyen", value: "adyen" },
        { label: "Intersoft (legacy)", value: "intersoft" },
        { label: "Paymob (legacy)", value: "paymob" },
      ],
      locations: [],
      customers: [],
      loading: false,
      formErrors: {},
    };
  },
  watch: {
    modal: function (value) {
      if (!value) {
        this.resetForm();
      } else {
        this.setInitialData();
      }
    },
    initialValue: function (value) {
      if (value) {
        this.setInitialData();
      }
    },
    form: {
      handler(newVal) {
        if (newVal["printer-settings"]) {
          this.form["printer-settings"]["printer-manufacturer"] = "sunmi";
          this.form["printer-settings"]["receipt-mode"] = "printer";
        }

        if (newVal["payment-gateway"] === "geidea-pax") {
          this.form.port = 6100;
        }

        if (newVal["payment-gateway"] === "geidea-verifone") {
          this.form.port = 80;
        }
      },
      deep: true,
    }
  },
  computed: {
    modal: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    locationOptions() {
      return this.mapLocationOptions(this.locations);
    },
    customersOptions() {
      return this.mapCustomerOptions(this.customers);
    },
    terminalOptions() {
      return this.form.type == "ipad" ? this.terminal : this.windowsTerminal;
    },
    locationPos() {
      return this.locations.find(
        (location) => Number(location.id) === Number(this.form["location-id"])
      )?.attributes?.pos;
    },
    isCustomerRequired() {
      const locationPos = this.locations.find(
        (location) => Number(location.id) === Number(this.form["location-id"])
      )?.attributes?.pos;

      if(locationPos) {
        const notRequiredPos = [
          "foodics-f5",
          "foodics",
          "ocost",
          "sdm",
          "revel",
          "posist",
        ];
        return !notRequiredPos.includes(locationPos);
      }
      
      return true;
    },
  },
  methods: {
    mapCustomerOptions(customers) {
      return customers.map((customer) => {
        return {
          label: `${customer.attributes["first-name"]} ${customer.attributes["last-name"]}`,
          value: customer.id,
        };
      });
    },
    mapLocationOptions(locations) {
      return locations.sort((a, b) => (a.attributes.name > b.attributes.name ? 1 : -1)).map((location) => {
        return {
          label: location.attributes.name,
          value: location.id,
        };
      });
    },
    async getLocations() {
      try {
        const response = await LocationService.fetchLocationsActive("en-us", 1);
        this.locations = response.data.data;
      } catch (error) {
        this.systemErrorNotification(
          "NOTIFICATION",
          "Failed to retrieve locations"
        );
      }
    },
    getCustomers() {
      Customer.all()
        .then((response) => {
          this.customers = response.data.data;
        })
        .catch(() => {
          this.systemErrorNotification(
            "NOTIFICATION",
            "Failed to retrieve customers"
          );
        });
    },
    async updateDevice() {
      await Device.update(this.form.id, this.form);
    },
    async createDevice() {
      await Device.create(this.form);
    },
    async validateForm() {
      let newErrors = {};

      if (this.form.label.trim() === "") {
        newErrors.label = this.translate("Label is required");
      }

      if (this.form["location-id"].trim() === "") {
        newErrors.location = this.translate("Location is required");
      }

      if (this.form.type === "") {
        newErrors.password = this.translate("Type is required");
      }

      if (this.form.uuid === "" && this.form.type === "windows") {
        newErrors.uuid = this.translate("Signage OS UID is required");
      }

      if (this.isCustomerRequired && this.form["customer-id"] === "") {
        newErrors.customer = this.translate("Customer is required");
      }

      this.formErrors = newErrors;

      return Object.keys(newErrors).length === 0;
    },
    async submitForm() {
      const isValid = await this.validateForm();

      if (!isValid) {
        return false;
      }

      this.loading = true;
      try {
        if (this.form.id) {
          await this.updateDevice();
        } else {
          await this.createDevice();
        }

        this.trackAnalytics();
        this.$emit("onSucess");
      } catch (error) {
        this.$emit("onError", error);
      }
      this.loading = false;
    },
    trackAnalytics() {
      const eventName = this.form.id ? "update_kiosk" : "create_kiosk";

      const locationId = this.form["location-id"];

      const deviceText = this.form["type"]
        ? this.devices.find((device) => device.value === this.form["type"])
            ?.text || null
        : null;

      const printerManufacturerText = this.form["printer-settings"]?.[
        "printer-manufacturer"
      ]
        ? this.manufacturers.find(
            (manufacturer) =>
              manufacturer.value ===
              this.form["printer-settings"]["printer-manufacturer"]
          )?.text || null
        : null;

      const receiptModeText = this.form["printer-settings"]?.["receipt-mode"]
        ? this.modes.find(
            (mode) =>
              mode.value === this.form["printer-settings"]["receipt-mode"]
          )?.text || null
        : null;

      let cardPaymentTerminalText = null;
      if (this.form["payment-gateway"]) {
        const terminals =
          this.form["type"] === "windows"
            ? this.windowsTerminal
            : this.terminal;
        cardPaymentTerminalText =
          terminals.find((term) => term.value === this.form["payment-gateway"])
            ?.text || null;
      }

      analytics.track(eventName, {
        location_id: locationId,
        device: deviceText,
        printer_manufacturer: printerManufacturerText,
        receipt_mode: receiptModeText,
        card_payment_terminal: cardPaymentTerminalText,
      });
    },
    resetForm() {
      this.form = cloneDeep(defaultForm);
    },
    setInitialData() {
      const attrs = this.initialValue?.attributes || {};

      const flat = {
        id: String(this.initialValue?.id || ""),
        label: attrs.label,
        status: attrs.status,
        available: attrs.available,
        "location-id": String(attrs["location-id"] || ""),
        "customer-id": String(attrs["customer-id"] || ""),
        uuid: attrs["device-uuid"],
        "ip-address": attrs["ip-address"],
        "terminal-id": attrs["terminal-id"],
        type: attrs.type,
        port: attrs.port,
        "payment-gateway": attrs["payment-gateway"],
        "is-settings-password-protected": !!attrs["is-settings-password-protected"],
        "is-maintenance-mode": !!attrs["is-maintenance-mode"],
      };

      this.form = {
        ...cloneDeep(defaultForm),
        ...flat,
        "printer-settings": {
          ...defaultForm["printer-settings"],
          ...(attrs["printer-settings"] || {}),
        },
        "payment-settings": {
          ...defaultForm["payment-settings"],
          ...(attrs["payment-settings"] || {}),
        },
      };
    },
    searchCustomer(search, callback) {
      Customer.searchInKiosk(search).then((response) => {
        const results = response?.data?.data;
        const options = this.mapCustomerOptions(results);

        callback(options);
      });
    },
    searchLocation(search, callback) {
      Customer.searchLocInKiosk(search, 'en-us').then((response) => {
        const results = response?.data?.data;
        const options = this.mapLocationOptions(results);
        
        callback(options);
      });
    },
    deactivateKiosk() {
      this.$emit('deactivate-kiosk', this.initialValue);
    },
  },
  created() {
    this.debounceSearchCustomer = _.debounce((search, callback) => {
      this.searchCustomer(search, callback);
    }, 1000);

    this.debounceSearchLocation = _.debounce((search, callback) => {
      this.searchLocation(search, callback);
    }, 1000);
  },
  mounted() {
    this.getLocations();
    this.getCustomers();
  },
};
</script>
